import { Capacitor } from '@capacitor/core';
import logger from '../utils/logger';

const iapLogger = logger.module('IAP');

// Check platform FIRST before importing RevenueCat (it doesn't support web)
const platform = Capacitor.getPlatform();
const isWebPlatform = platform === 'web';

// Only import RevenueCat on native platforms
// These will be undefined on web
let Purchases: any = null;
let LOG_LEVEL: any = null;

if (!isWebPlatform) {
  try {
    // Dynamic import to avoid loading on web
    const purchasesModule = require('@revenuecat/purchases-capacitor');
    const internalModule = require('@revenuecat/purchases-typescript-internal-esm');
    Purchases = purchasesModule.Purchases;
    LOG_LEVEL = internalModule.LOG_LEVEL;
  } catch (e) {
    iapLogger.warn('RevenueCat not available on this platform');
  }
}

// RevenueCat API Key - use platform-specific key (only needed on native)
const SDK_KEY = isWebPlatform ? '' : (
  platform === 'android'
    ? (process.env.REACT_APP_REVENUECAT_ANDROID_API_KEY || '')
    : (process.env.REACT_APP_REVENUECAT_IOS_API_KEY || '')
);

/**
 * Simple IAP Service following the original Marune app pattern
 * - Single initialization point
 * - Direct RevenueCat SDK usage
 * - No complex abstractions
 */
class SimpleIAPService {
  private isInitialized = false;
  private currentUserId: string | null = null;

  /**
   * Initialize RevenueCat SDK with user ID
   * Following original app pattern: setup once on login
   */
  async initialize(uid: string): Promise<void> {
    // Skip initialization on web platform
    if (isWebPlatform) {
      iapLogger.info('IAP not available on web platform');
      return;
    }

    // Prevent re-initialization with same user
    if (this.isInitialized && this.currentUserId === uid) {
      iapLogger.info('IAP already initialized for user:', uid);
      return;
    }

    if (!Purchases) {
      iapLogger.warn('RevenueCat SDK not loaded');
      return;
    }

    try {
      iapLogger.info('Initializing IAP module for user:', uid);

      // Enable debug logs
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

      // Configure RevenueCat - matches original app's setup() call
      await Purchases.configure({
        apiKey: SDK_KEY,
        appUserID: uid
      });

      this.isInitialized = true;
      this.currentUserId = uid;

      iapLogger.info('✅ IAP module initialized successfully');
    } catch (error) {
      iapLogger.error('Failed to initialize IAP:', error);
      throw error;
    }
  }

  /**
   * Get current offerings from RevenueCat
   * Matches original app's getOfferings()
   */
  async getOfferings(): Promise<any[]> {
    // Return empty on web platform
    if (isWebPlatform || !Purchases) {
      return [];
    }

    if (!this.isInitialized) {
      iapLogger.warn('IAP not initialized, cannot get offerings');
      return [];
    }

    try {
      const offerings = await Purchases.getOfferings();

      if (offerings.current && offerings.current.availablePackages) {
        iapLogger.info(`Found ${offerings.current.availablePackages.length} packages`);
        return offerings.current.availablePackages;
      }

      iapLogger.warn('No current offering available');
      return [];
    } catch (error) {
      iapLogger.error('Error fetching offerings:', error);
      return [];
    }
  }

  /**
   * Get customer info (subscription status)
   * Matches original app's getSubscriptionStatus()
   */
  async getCustomerInfo(): Promise<any | null> {
    // Return null on web platform
    if (isWebPlatform || !Purchases) {
      return null;
    }

    if (!this.isInitialized) {
      iapLogger.warn('IAP not initialized, cannot get customer info');
      return null;
    }

    try {
      const { customerInfo } = await Purchases.getCustomerInfo();

      // Comprehensive logging for debugging subscription issues
      if (customerInfo) {
        const activeEntitlements = Object.keys(customerInfo.entitlements.active || {});
        const allEntitlements = Object.keys(customerInfo.entitlements.all || {});
        const peakEntitlement = customerInfo.entitlements.active['peak'];
        const peakAllEntitlement = customerInfo.entitlements.all['peak'];

        iapLogger.info('Customer info fetched:', {
          activeEntitlements,
          allEntitlements,
          peakActive: peakEntitlement?.isActive,
          peakProductId: peakEntitlement?.productIdentifier,
          peakExpiresDate: peakEntitlement?.expirationDate,
          peakWillRenew: peakEntitlement?.willRenew,
          // Also check "all" entitlements (includes expired)
          peakInAll: !!peakAllEntitlement,
          peakAllActive: peakAllEntitlement?.isActive,
          allPurchasedProducts: customerInfo.allPurchasedProductIdentifiers,
          // Check for any active subscriptions
          activeSubscriptions: customerInfo.activeSubscriptions,
          // Log the entire entitlements object for debugging
          rawActiveEntitlements: customerInfo.entitlements.active
        });
      } else {
        iapLogger.warn('getCustomerInfo returned null customerInfo');
      }

      return customerInfo;
    } catch (error) {
      iapLogger.error('Error fetching customer info:', error);
      return null;
    }
  }

  /**
   * Purchase a package
   * Matches original app's purchasePackage()
   */
  async purchasePackage(packageToPurchase: any): Promise<{ success: boolean; error?: any; customerInfo?: any }> {
    // Not available on web
    if (isWebPlatform || !Purchases) {
      return {
        success: false,
        error: new Error('IAP not available on this platform')
      };
    }

    if (!this.isInitialized) {
      return {
        success: false,
        error: new Error('IAP not initialized')
      };
    }

    try {
      iapLogger.info('Attempting to purchase package:', packageToPurchase.identifier);

      // Check if user already has this entitlement before attempting purchase
      const preCheckInfo = await this.getCustomerInfo();
      if (preCheckInfo?.entitlements.active['peak']?.isActive) {
        iapLogger.info('User already has peak subscription, returning current info');
        return { success: true, customerInfo: preCheckInfo };
      }

      // Make the purchase
      const result = await Purchases.purchasePackage({
        aPackage: packageToPurchase
      });

      iapLogger.info('✅ Purchase successful, fetching updated customer info');

      // Get updated customer info after purchase
      // Using result.customerInfo if available, otherwise fetch it
      const customerInfo = result.customerInfo || await this.getCustomerInfo();

      return { success: true, customerInfo: customerInfo || undefined };
    } catch (error: any) {
      // Check for specific error codes
      if (error.userCancelled) {
        iapLogger.info('User cancelled purchase');
        return { success: false, error };
      }

      // Check for "already purchased" error
      if (error.code === 'PRODUCT_ALREADY_PURCHASED' || error.message?.includes('already')) {
        iapLogger.info('Product already purchased, fetching current customer info');
        const customerInfo = await this.getCustomerInfo();
        return { success: true, customerInfo: customerInfo || undefined };
      }

      iapLogger.error('Purchase failed:', error);
      return { success: false, error };
    }
  }

  /**
   * Restore purchases
   * Matches original app's restorePurchases()
   */
  async restorePurchases(): Promise<{ success: boolean; error?: any }> {
    // Not available on web
    if (isWebPlatform || !Purchases) {
      return {
        success: false,
        error: new Error('IAP not available on this platform')
      };
    }

    if (!this.isInitialized) {
      return {
        success: false,
        error: new Error('IAP not initialized')
      };
    }

    try {
      iapLogger.info('Restoring purchases...');
      await Purchases.restorePurchases();
      iapLogger.info('✅ Purchases restored successfully');
      return { success: true };
    } catch (error) {
      iapLogger.error('Failed to restore purchases:', error);
      return { success: false, error };
    }
  }

  /**
   * Add listener for customer info updates
   * Matches original app's onPurchasesUpdated()
   */
  addCustomerInfoUpdateListener(callback: (info: any) => void): void {
    // Not available on web
    if (isWebPlatform || !Purchases) {
      iapLogger.info('Customer info listener not available on web platform');
      return;
    }

    Purchases.addCustomerInfoUpdateListener((customerInfo: any) => {
      iapLogger.info('Customer info updated via listener');

      // Log entitlements for debugging
      const activeEntitlements = Object.keys(customerInfo.entitlements.active || {});
      const peakActive = customerInfo.entitlements.active['peak']?.isActive;
      iapLogger.info('Listener customer info:', {
        activeEntitlements,
        peakActive,
        allPurchasedProducts: customerInfo.allPurchasedProductIdentifiers
      });

      callback(customerInfo);
    });
  }

  /**
   * Check if user has "peak" entitlement
   */
  async hasPeakEntitlement(): Promise<boolean> {
    const customerInfo = await this.getCustomerInfo();
    if (!customerInfo) return false;

    return customerInfo.entitlements.active['peak']?.isActive || false;
  }

  /**
   * Reset IAP state (for logout)
   */
  reset(): void {
    this.isInitialized = false;
    this.currentUserId = null;
    iapLogger.info('IAP service reset');
  }
}

// Export singleton instance
export const simpleIAPService = new SimpleIAPService();